import * as React from 'react'
import { Link, useSearchParams } from 'react-router'
import { AtSign, Globe, Inbox, Loader2, Mail, RefreshCw, Send, X } from 'lucide-react'
import type { AllowlistEntryDto, CapturedMailListItemDto } from '@lib/dto/test-mail.dto'
import type { MailDeliveryStatus } from '@lib/enums/mail-delivery-status.enum'
import { allowlistEntryKindSchema, type AllowlistEntryKind } from '@lib/enums/allowlist-entry.enum'
import { addAllowlistEntryInputSchema, testMailMessagesQuerySchema } from '@lib/input/test-mail.input'
import { uuidParamSchema, type Uuid } from '@lib/validators/params'
import { CopyButton } from '@/components/CopyButton'
import { PageHeader } from '@/components/PageHeader'
import { RecordFacts, RecordHeadline, RecordSheet } from '@/components/RecordSheet'
import { EmptyState, ErrorState, LoadingState } from '@/components/PageState'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Field, fieldMessageId } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { toast } from '@/components/ui/toast'
import { Tooltip } from '@/components/ui/tooltip'
import { canEditSettings, useDashboardScope } from '@/dashboard/DashboardScope'
import { ReadOnlyBanner } from '@/dashboard/ReadOnlyBanner'
import { apiErrorMessage } from '@/helpers/apiErrorMessage'
import { useAllowlist, useAllowlistActions, useCapturedMail, useMailbox } from '@/hooks/useTestMail'
import { cn } from '@/lib/utils'

const STATUS_LABELS: Record<MailDeliveryStatus, { label: string; tone: 'neutral' | 'success' | 'danger' }> = {
  captured: { label: 'Captured', tone: 'neutral' },
  delivered: { label: 'Delivered', tone: 'success' },
  delivery_failed: { label: 'Delivery failed', tone: 'danger' },
}

/** Links in the previewed email open in a new tab. The base tag goes in <head>, so the email keeps its own doctype. */
function withNewTabLinks(html: string): string {
  const base = '<base target="_blank">'
  const head = /<head[^>]*>/i.exec(html)
  if (!head) return base + html
  const at = head.index + head[0].length
  return html.slice(0, at) + base + html.slice(at)
}

interface EntryRowProps {
  entry: AllowlistEntryDto
  canEdit: boolean
  actions: ReturnType<typeof useAllowlistActions>
}

/** One allowlisted address or domain, with what it still needs to become active. */
function EntryRow({ entry, canEdit, actions }: EntryRowProps) {
  // This row's own request, so a check or removal on one row neither disables
  // the others nor can be sent twice from this one.
  const [busy, setBusy] = React.useState<'resend' | 'verify' | 'remove' | null>(null)

  async function run<T>(kind: 'resend' | 'verify' | 'remove', action: () => Promise<T>, success: string): Promise<void> {
    setBusy(kind)
    try {
      await action()
      toast.success(success)
    } catch (failure) {
      toast.error(apiErrorMessage(failure))
    } finally {
      setBusy(null)
    }
  }

  function renderPending() {
    if (entry.kind === 'address') {
      return (
        <p className="text-caption text-muted-foreground">
          Waiting for {entry.value} to click the confirmation link we emailed.
          {canEdit ? (
            <Button
              variant="link"
              size="sm"
              className="h-auto px-1 text-caption"
              disabled={busy !== null}
              onClick={() => void run('resend', () => actions.resend.mutateAsync(entry.id), `Sent a new link to ${entry.value}`)}
            >
              Resend
            </Button>
          ) : null}
        </p>
      )
    }
    if (!entry.dnsRecord) return null
    return (
      <div className="space-y-2">
        <p className="text-caption text-muted-foreground">
          {entry.dnsLapsedAt
            ? `Paused on ${new Date(entry.dnsLapsedAt).toLocaleDateString()}: the TXT record was gone at the weekly check, so test email to this domain stopped. Put the record back, then check. It also resumes on its own at a later weekly check.`
            : 'Publish this TXT record, then check:'}
        </p>
        <dl className="grid grid-cols-[4rem_minmax(0,1fr)_auto] items-center gap-x-2 gap-y-1 rounded-md bg-subtle px-3 py-2 font-mono text-caption">
          <dt className="text-muted-foreground">Name</dt>
          <dd className="truncate">{entry.dnsRecord.name}</dd>
          <CopyButton value={entry.dnsRecord.name} label="Record name" />
          <dt className="text-muted-foreground">Value</dt>
          <dd className="truncate">{entry.dnsRecord.value}</dd>
          <CopyButton value={entry.dnsRecord.value} label="Record value" />
        </dl>
        {canEdit ? (
          <Button
            variant="secondary"
            size="sm"
            disabled={busy !== null}
            onClick={() => void run('verify', () => actions.verify.mutateAsync(entry.id), `${entry.value} verified`)}
          >
            {busy === 'verify' ? <Loader2 className="animate-spin" /> : <RefreshCw />}
            Check DNS now
          </Button>
        ) : null}
      </div>
    )
  }

  function renderBadge() {
    if (entry.status === 'active') return <Badge tone="success">Active</Badge>
    return <Badge tone="warning">{entry.dnsLapsedAt ? 'Paused' : 'Pending'}</Badge>
  }

  const Icon = entry.kind === 'address' ? AtSign : Globe
  return (
    <li className="flex items-start gap-3 py-3">
      <Icon className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
      <div className="min-w-0 flex-1 space-y-1">
        <p className="flex items-center gap-2">
          <span className="truncate font-medium">{entry.kind === 'domain' ? `Anyone @${entry.value}` : entry.value}</span>
          {renderBadge()}
        </p>
        {entry.status === 'pending' ? renderPending() : null}
      </div>
      {canEdit ? (
        <Tooltip content="Remove">
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={`Remove ${entry.value}`}
            disabled={busy !== null}
            onClick={() => void run('remove', () => actions.remove.mutateAsync(entry.id), `Removed ${entry.value}`)}
          >
            {busy === 'remove' ? <Loader2 className="animate-spin" /> : <X />}
          </Button>
        </Tooltip>
      ) : null}
    </li>
  )
}

/** Who really receives test email; everything else stays in the mailbox below. */
function AllowlistCard() {
  const scope = useDashboardScope()
  const query = useAllowlist()
  const actions = useAllowlistActions()
  const canEdit = canEditSettings(scope)
  const [kind, setKind] = React.useState<AllowlistEntryKind>('address')
  const [value, setValue] = React.useState('')
  const [error, setError] = React.useState<string | null>(null)

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault()
    const parsed = addAllowlistEntryInputSchema.safeParse({ kind, value })
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Check the value')
      return
    }
    setError(null)
    try {
      await actions.add.mutateAsync(parsed.data)
      toast.success(kind === 'address' ? `Confirmation sent to ${parsed.data.value}` : `Added ${parsed.data.value}`)
      setValue('')
    } catch (failure) {
      setError(apiErrorMessage(failure, 'Could not add it.'))
    }
  }

  function renderEntries() {
    if (query.isLoading) return <LoadingState />
    if (query.isError || !query.data) {
      return <ErrorState message={apiErrorMessage(query.error, 'Could not load the allowlist.')} />
    }
    if (query.data.entries.length === 0) {
      return <p className="text-body text-muted-foreground">Nobody yet: every test email stays in the mailbox.</p>
    }
    return (
      <ul className="divide-y divide-border">
        {query.data.entries.map((entry) => (
          <EntryRow key={entry.id} entry={entry} canEdit={canEdit} actions={actions} />
        ))}
      </ul>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Who receives test email</CardTitle>
        <CardDescription>
          Test emails reach only these addresses, marked [TEST]. An address confirms by clicking a link we email it; a
          domain by a DNS record. Public mailbox domains (Gmail, Outlook…) can only be added as single addresses.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {renderEntries()}
        {canEdit ? (
          <form onSubmit={handleSubmit} noValidate className="flex flex-wrap items-start gap-3 border-t border-border pt-4">
            <Field label="Type" htmlFor="allowlist-kind" className="w-32">
              <Select
                id="allowlist-kind"
                value={kind}
                onChange={(event) => {
                  const next = allowlistEntryKindSchema.safeParse(event.target.value)
                  if (next.success) setKind(next.data)
                }}
              >
                <option value="address">Address</option>
                <option value="domain">Domain</option>
              </Select>
            </Field>
            <Field
              label={kind === 'address' ? 'Email address' : 'Domain'}
              htmlFor="allowlist-value"
              error={error}
              className="min-w-56 flex-1"
            >
              <Input
                id="allowlist-value"
                autoComplete="off"
                placeholder={kind === 'address' ? 'qa@yourcompany.com' : 'yourcompany.com'}
                value={value}
                aria-invalid={error ? true : undefined}
                aria-describedby={fieldMessageId('allowlist-value')}
                onChange={(event) => setValue(event.target.value)}
              />
            </Field>
            <Button type="submit" disabled={actions.add.isPending} className="sm:mt-[calc(var(--ds-control-text)+0.375rem)]">
              {actions.add.isPending ? <Loader2 className="animate-spin" /> : <Send />}
              Add
            </Button>
          </form>
        ) : null}
      </CardContent>
    </Card>
  )
}

interface MessageListProps {
  messages: CapturedMailListItemDto[]
  selectedId: string | null
  onSelect: (id: string) => void
}

function MessageList({ messages, selectedId, onSelect }: MessageListProps) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>To</TableHead>
          <TableHead>Subject</TableHead>
          <TableHead>Envelope</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>When</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {messages.map((message) => {
          const status = STATUS_LABELS[message.status]
          return (
            <TableRow
              key={message.id}
              data-record-id={message.id}
              className={cn('cursor-pointer', message.id === selectedId && 'bg-selected')}
              onClick={() => onSelect(message.id)}
            >
              <TableCell className="max-w-56 truncate">{message.recipient}</TableCell>
              <TableCell className="max-w-sm">
                <button type="button" className="block w-full truncate text-left font-medium text-link">
                  {message.subject}
                </button>
              </TableCell>
              <TableCell className="max-w-56 truncate text-muted-foreground">{message.envelopeTitle}</TableCell>
              <TableCell>
                <Badge tone={status.tone}>{status.label}</Badge>
              </TableCell>
              <TableCell className="whitespace-nowrap text-muted-foreground">
                {new Date(message.createdAt).toLocaleString()}
              </TableCell>
            </TableRow>
          )
        })}
      </TableBody>
    </Table>
  )
}

interface MessageViewerProps {
  messageId: Uuid
  envelopeLink: (envelopeId: string) => string
}

/** One captured email as the recipient would see it. Links open in a new tab and work (they're test links). */
function MessageViewer({ messageId, envelopeLink }: MessageViewerProps) {
  const query = useCapturedMail(messageId)
  const [asText, setAsText] = React.useState(false)

  if (query.isLoading) return <LoadingState />
  if (query.isError || !query.data) {
    return (
      <div className="p-card">
        <ErrorState message={apiErrorMessage(query.error, 'Could not load the message.')} />
      </div>
    )
  }
  const message = query.data
  const status = STATUS_LABELS[message.status]
  return (
    <>
      <RecordHeadline
        facts={
          <>
            <RecordFacts>
              <dt className="text-muted-foreground">To</dt>
              <dd className="truncate">{message.recipient}</dd>
              <dt className="text-muted-foreground">Envelope</dt>
              <dd className="truncate">
                <Link to={envelopeLink(message.envelopeId)} className="text-link hover:underline">
                  {message.envelopeTitle}
                </Link>
              </dd>
              <dt className="text-muted-foreground">Sent</dt>
              <dd>{new Date(message.createdAt).toLocaleString()}</dd>
            </RecordFacts>
            {message.error ? <p className="text-caption text-danger">{message.error}</p> : null}
          </>
        }
      >
        <h3 className="text-body-lg font-semibold">{message.subject}</h3>
        <Badge tone={status.tone}>{status.label}</Badge>
        {message.html ? (
          <Button variant="secondary" size="sm" className="ml-auto" onClick={() => setAsText(!asText)}>
            {asText ? 'Show HTML' : 'Show text'}
          </Button>
        ) : null}
      </RecordHeadline>
      {message.html && !asText ? (
        <iframe
          aria-label={`Email: ${message.subject}`}
          className="h-[36rem] w-full bg-card"
          sandbox="allow-popups allow-popups-to-escape-sandbox"
          srcDoc={withNewTabLinks(message.html)}
        />
      ) : (
        <pre className="p-card font-mono text-caption whitespace-pre-wrap">{message.text}</pre>
      )}
    </>
  )
}

/**
 * Test mode's mailbox: every email test envelopes sent, whether or not it was
 * delivered, plus the allowlist of real recipients.
 */
export function MailboxPage() {
  const scope = useDashboardScope()
  const [params, setParams] = useSearchParams()
  // The list filter is the API's own query, and the open message is a route
  // id. A hand-edited value that doesn't fit is ignored, the same way other
  // pages drop a bad search param.
  const rawEnvelopeId = params.get('envelopeId')
  const filter = testMailMessagesQuerySchema.safeParse(rawEnvelopeId ? { envelopeId: rawEnvelopeId } : {})
  const envelopeId = filter.success ? (filter.data.envelopeId ?? null) : null
  const messageParsed = uuidParamSchema.safeParse(params.get('message'))
  const selectedId = messageParsed.success ? messageParsed.data : null
  const query = useMailbox(envelopeId)

  const select = React.useCallback(
    (id: string | null, options: { replace: boolean } = { replace: false }) =>
      setParams((current) => {
        const next = new URLSearchParams(current)
        if (id) next.set('message', id)
        else next.delete('message')
        return next
      }, options),
    [setParams],
  )

  if (scope.mode !== 'test') {
    return (
      <>
        <PageHeader title="Mailbox" />
        <EmptyState
          icon={Inbox}
          title="The mailbox is for test mode"
          description="Live emails go to their recipients. Switch to test mode to see what test envelopes sent."
          action={
            <Button variant="secondary" size="sm" onClick={() => scope.setMode('test')}>
              Switch to test mode
            </Button>
          }
        />
      </>
    )
  }

  function renderMessages() {
    if (query.isLoading) return <LoadingState />
    if (query.isError || !query.data) {
      return <ErrorState message={apiErrorMessage(query.error, 'Could not load the mailbox.')} />
    }
    if (query.data.messages.length === 0) {
      return (
        <EmptyState
          icon={Mail}
          title={envelopeId ? 'No emails for this envelope' : 'No test emails yet'}
          description="Emails from envelopes created with a test key appear here."
        />
      )
    }
    const messages = query.data.messages
    return (
      <>
        <MessageList messages={messages} selectedId={selectedId} onSelect={select} />
        <RecordSheet title="Email" ids={messages.map((message) => message.id)} selectedId={selectedId} onSelect={select}>
          {(id) => <MessageViewer messageId={id} envelopeLink={(envelopeId) => `${scope.basePath}/envelopes/${envelopeId}`} />}
        </RecordSheet>
      </>
    )
  }

  return (
    <>
      <PageHeader
        title="Mailbox"
        description="Every email your test envelopes sent. Only allowlisted recipients actually received one."
      />
      <ReadOnlyBanner />
      <div className="space-y-8">
        <AllowlistCard />
        <section className="space-y-3">
          <div className="flex items-center justify-between gap-2">
            <h2 className="text-title font-semibold">Emails</h2>
            {envelopeId ? (
              <Button variant="ghost" size="sm" onClick={() => setParams(new URLSearchParams())}>
                Show all envelopes
              </Button>
            ) : null}
          </div>
          {renderMessages()}
        </section>
      </div>
    </>
  )
}
